<template>
  <e-dialog ref="dialogRef" @after-leave="onDialogClose">
    <template #activator>
      <e-btn variant="primary" class="users__add-btn" :icon="$vuetify.display.smAndDown">
        <template v-if="!$vuetify.display.smAndDown" #prepend>
          <e-icon name="plus" />
        </template>

        <e-icon v-if="$vuetify.display.smAndDown" name="plus" />

        <span v-if="!$vuetify.display.smAndDown"> Пригласить пользователей </span>
      </e-btn>
    </template>

    <template #title>
      <span class="caption">Добавление пользователей</span>
    </template>

    <div class="paragraph-m_h text-secondary">
      Отправьте приглашение пользователям на&nbsp;почту. После того как пользователи примут приглашение
      и&nbsp;зарегистрируются в системе вы&nbsp;получите уведомление и&nbsp;назначите ему роль
    </div>

    <e-tags-input
      v-model="emails"
      v-model:is-valid="isValidEmails"
      ref="tagsInputRef"
      :validation-function="validationFunction"
      :rules="rules"
      :error-messages="errorMessage"
      :delimiters="[',', ' ']"
      details-text="Используйте для ввода Enter запятую или пробел"
      label="Укажите адреса электронной почты"
      min-height="200px"
      class="mt-6"
    />

    <template #buttons>
      <e-btn :loading="isLoading" variant="primary" class="ml-auto w-100 w-sm-auto" @click="submit">
        <template #prepend>
          <e-icon name="messageSend" />
        </template>
        Отправить приглашения
      </e-btn>
    </template>
  </e-dialog>
</template>

<script setup lang="ts">
import { ref, toRef, watch } from 'vue';
import type { IDialog, ITagsInputComponent } from '@/core/types/components/components';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, EIcon, ETagsInput } from '@/core/components/ui';
import { useSnackbar } from '@/core/composables/useSnackbar';
import { declensionString, isValidEmail } from '@/core/utils/string';
import { required } from '@/core/utils/validation';
import { useUsersStore } from '@/parts/users/store/users';

const emit = defineEmits<{
  (event: 'submit'): void;
}>();

const emails = ref([]);
const isValidEmails = ref(false);

const validationFunction = (item: string): boolean => isValidEmail(item);

const rules = [
  required(),
  () => (!validAddresses.value.length ? 'Нет валидных адресов для отправки приглашения' : true),
];

const dialogRef = ref<IDialog>();
const tagsInputRef = ref<ITagsInputComponent>();
const isLoading = ref(false);
const { inviteUsers } = useUsersStore();

const errorMessage = ref('');

const { showSnackbar } = useSnackbar();

const validAddresses = toRef(() => emails.value.filter((email) => validationFunction(email)));

watch(
  () => emails.value.length,
  () => {
    if (emails.value.find((item) => !validationFunction(item))) {
      errorMessage.value = 'Некоторые адреса содержат ошибки, сообщения на них не будут отправлены';
    } else {
      errorMessage.value = '';
    }
  },
);

const submit = async () => {
  tagsInputRef.value?.validate();

  if (!isValidEmails.value) {
    return;
  }

  isLoading.value = true;

  // TODO: После удаления не забыть добавить работу с репликами
  await inviteUsers(validAddresses.value);

  if (validAddresses.value.length < emails.value.length) {
    showSnackbar({
      type: 'info',
      title: 'Приглашение отправлено',
      description: `на ${validAddresses.value.length} из ${emails.value.length} ${declensionString(emails.value.length, ['почтового адреса', 'почтовых адресов', 'почтовых адресов'])}`,
    });
  } else {
    showSnackbar({
      type: 'success',
      title: 'Приглашение отправлено',
      description: `на ${emails.value.length} ${declensionString(emails.value.length, ['почтовый адрес', 'почтовых адреса', 'почтовых адресов'])}`,
    });
  }

  dialogRef.value?.close();

  isLoading.value = false;

  emit('submit');
};

const onDialogClose = () => {
  emails.value = [];
};
</script>

<style scoped lang="scss">
.users {
  &__add-btn {
    @include sm-and-down {
      position: fixed;
      right: 24px;
      bottom: 24px;
    }
    @include xs {
      right: 16px;
      bottom: 16px;
    }
  }
}
</style>
